import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Check, Loader2, HardDrive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Downloads = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [downloaded, setDownloaded] = useState({});

  useEffect(() => {
    fetchThemes();
    loadDownloadedStatus();
  }, []);

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API_URL}/themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThemes(response.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadDownloadedStatus = () => {
    // Charger depuis localStorage quels fichiers ont été téléchargés
    const saved = localStorage.getItem('downloaded_audios');
    if (saved) {
      setDownloaded(JSON.parse(saved));
    }
  };

  const saveDownloadedStatus = (newStatus) => {
    localStorage.setItem('downloaded_audios', JSON.stringify(newStatus));
    setDownloaded(newStatus);
  };

  const downloadAudio = async (slug, mode, title) => {
    const key = `${slug}-${mode}`;
    setDownloading(prev => ({ ...prev, [key]: true }));

    try {
      const response = await axios.get(
        `${API_URL}/audio/stream/${slug}/${mode}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title} - ${mode === 'zen' ? 'Zen' : 'Mouvement'}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Marquer comme téléchargé
      const newStatus = { ...downloaded, [key]: true };
      saveDownloadedStatus(newStatus);
      
      toast.success(`${title} (${mode}) téléchargé !`);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const downloadAll = async () => {
    for (const theme of themes) {
      if (!downloaded[`${theme.slug}-zen`]) {
        await downloadAudio(theme.slug, 'zen', theme.title);
        await new Promise(r => setTimeout(r, 500));
      }
      if (!downloaded[`${theme.slug}-movement`]) {
        await downloadAudio(theme.slug, 'movement', theme.title);
        await new Promise(r => setTimeout(r, 500));
      }
    }
    toast.success('Tous les audios ont été téléchargés !');
  };

  const clearDownloads = () => {
    localStorage.removeItem('downloaded_audios');
    setDownloaded({});
    toast.success('Liste des téléchargements effacée');
  };

  const countDownloaded = () => {
    return Object.values(downloaded).filter(Boolean).length;
  };

  const countAvailable = () => {
    return themes.length * 2;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-500 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-sand-50" data-testid="downloads-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-sage-500 to-sage-600 px-6 py-8 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white hover:bg-white/10 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-3xl font-serif font-light mb-2">
          Téléchargements
        </h1>
        <p className="text-white/80 font-sans">Écoutez hors-ligne</p>
      </div>

      {/* Info */}
      <div className="px-6 py-4">
        <div className="bg-sage-50 border border-sage-200 rounded-xl p-4">
          <div className="flex items-start">
            <HardDrive className="w-5 h-5 text-sage-500 mr-3 mt-0.5" />
            <div>
              <p className="text-sage-700 text-sm font-medium mb-1">
                Téléchargez vos audios
              </p>
              <p className="text-sage-500 text-xs">
                Les fichiers seront sauvegardés sur votre appareil pour une écoute sans internet. Taille totale : environ 150 Mo pour les 22 séances.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-6 pb-4">
        <div className="bg-white rounded-xl border border-sand-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-sage-700 font-medium">{countDownloaded()} / {countAvailable()}</p>
            <p className="text-xs text-sage-500">fichiers téléchargés</p>
          </div>
          {countDownloaded() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearDownloads}
              className="text-sage-500"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Effacer
            </Button>
          )}
        </div>
      </div>

      {/* Download all */}
      <div className="px-6 pb-4">
        <Button
          onClick={downloadAll}
          className="w-full h-14 bg-declic hover:bg-declic/90 text-white rounded-xl"
        >
          <Download className="w-5 h-5 mr-2" />
          Tout télécharger
        </Button>
      </div>

      {/* Themes list */}
      <div className="px-6 space-y-3">
        {themes.map((theme) => (
          <div
            key={theme.id}
            className="bg-white rounded-xl border border-sand-200 p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-sage-700">{theme.title}</h3>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {/* Zen */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadAudio(theme.slug, 'zen', theme.title)}
                disabled={downloading[`${theme.slug}-zen`]}
                className={`h-10 ${
                  downloaded[`${theme.slug}-zen`]
                    ? 'border-green-300 bg-green-50 text-green-700'
                    : 'border-sage-200'
                }`}
              >
                {downloading[`${theme.slug}-zen`] ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : downloaded[`${theme.slug}-zen`] ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Zen
                  </>
                ) : (
                  <>
                      <Download className="w-4 h-4 mr-1" />
                      Zen
                    </>
                  )}
                </Button>

                {/* Movement */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadAudio(theme.slug, 'movement', theme.title)}
                  disabled={downloading[`${theme.slug}-movement`]}
                  className={`h-10 ${
                    downloaded[`${theme.slug}-movement`]
                      ? 'border-green-300 bg-green-50 text-green-700'
                      : 'border-declic/30'
                  }`}
                >
                  {downloading[`${theme.slug}-movement`] ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : downloaded[`${theme.slug}-movement`] ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Mouvement
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-1" />
                      Mouvement
                    </>
                  )}
                </Button>
              </div>
          </div>
        ))}
      </div>

      {/* Note */}
      <div className="px-6 py-6">
        <p className="text-xs text-sage-500 text-center">
          Les fichiers sont téléchargés dans le dossier de téléchargement de votre appareil.
        </p>
      </div>
    </div>
  );
};

export default Downloads;
